import {
  AirlineSeatIndividualSuite,
  LocalHospital,
  Person,
  WheelchairPickup,
  MedicalServices,
} from '@mui/icons-material'
import { Badge, Box, Chip, Paper, Stack, Typography } from '@mui/material'
import React from 'react'
import moment from 'moment'

const getMinutesInCentre = (arrivedAt) => {
  if (!arrivedAt) return null
  return Math.max(moment().diff(moment(arrivedAt), 'minutes'), 0)
}

const statusStyles = {
  Available: {
    bg: '#DCFCE7',
    color: '#166534',
    border: '#86EFAC',
  },
  Occupied: {
    bg: '#FEE2E2',
    color: '#991B1B',
    border: '#FCA5A5',
  },
  Cleaning: {
    bg: '#DBEAFE',
    color: '#1E3A8A',
    border: '#93C5FD',
  },
  'Out of Order': {
    bg: '#E2E8F0',
    color: '#334155',
    border: '#CBD5E1',
  },
}

const zoneTypeConfig = {
  Resus: {
    icon: MedicalServices,
    color: '#DC2626',
    bg: '#FEF2F2',
  },
  Majors: {
    icon: LocalHospital,
    color: '#EA580C',
    bg: '#FFF7ED',
  },
  Minors: {
    icon: AirlineSeatIndividualSuite,
    color: '#2563EB',
    bg: '#EFF6FF',
  },
  Waiting: {
    icon: Person,
    color: '#7C3AED',
    bg: '#F5F3FF',
  },
  Welfare: {
    icon: WheelchairPickup,
    color: '#16A34A',
    bg: '#F0FDF4',
  },
}

const TreatmentZoneCard = ({ zone, onClick }) => {
  const status = statusStyles[zone.status] || statusStyles.Available
  const type = zoneTypeConfig[zone.type] || zoneTypeConfig.Minors
  const Icon = type.icon

  const patientCount = zone.patients?.length || 0
  const capacity = Math.max(Number(zone.capacity || zone.maxCapacity) || 1, 1)
  const multi = capacity > 1
  const full = patientCount >= capacity

  return (
    <Paper
      elevation={0}
      onClick={() => onClick(zone)}
      sx={{
        p: 2,
        borderRadius: 4,
        cursor: 'pointer',
        border: `1px solid ${status.border}`,
        background:
          zone.status === 'Occupied'
            ? 'linear-gradient(135deg, #FFFFFF 0%, #FFF7F7 100%)'
            : '#FFFFFF',
        boxShadow: '0 10px 28px rgba(15,23,42,0.08)',
        transition: 'all 0.18s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 16px 36px rgba(15,23,42,0.14)',
        },
        flex: 1,
      }}
    >
      <Stack spacing={1.5} width="100%">
        <Stack direction="row" justifyContent="space-between" spacing={2}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Badge
              badgeContent={patientCount}
              color={patientCount ? 'error' : 'default'}
            >
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: 3,
                  backgroundColor: type.bg,
                  color: type.color,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Icon />
              </Box>
            </Badge>

            <Box>
              <Typography fontWeight={900} color="#0F172A">
                {zone.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {zone.type} • {patientCount}/{capacity}
              </Typography>
            </Box>
          </Stack>

          <Chip
            size="small"
            label={full ? 'Full' : zone.status}
            sx={{
              borderRadius: 999,
              backgroundColor: status.bg,
              color: status.color,
              fontWeight: 900,
              border: `1px solid ${status.border}`,
            }}
          />
        </Stack>

        {patientCount > 0 ? (
          <Stack spacing={1}>
            {zone.patients.slice(0, 3).map((patient) => {
              const minutesInCentre = getMinutesInCentre(patient.arrivedAt)

              return (
                <Box
                  key={patient.id}
                  sx={{
                    p: 1,
                    borderRadius: 2.5,
                    backgroundColor: '#F8FAFC',
                    border: '1px solid rgba(15,23,42,0.06)',
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" gap={1}>
                    <Box>
                      <Typography variant="body2" fontWeight={900}>
                        {patient.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {patient.complaint}
                      </Typography>
                    </Box>

                    <Stack alignItems="flex-end" spacing={0.5}>
                      <Chip
                        size="small"
                        label={patient.priority}
                        color={
                          patient.priority === 'Critical'
                            ? 'error'
                            : patient.priority === 'High'
                              ? 'warning'
                              : 'default'
                        }
                      />

                      {minutesInCentre !== null && (
                        <Typography
                          variant="caption"
                          fontWeight={900}
                          color="text.secondary"
                          whiteSpace="nowrap"
                        >
                          {minutesInCentre}m in centre
                        </Typography>
                      )}
                    </Stack>
                  </Stack>
                </Box>
              )
            })}

            {patientCount > 3 && (
              <Typography variant="caption" color="text.secondary">
                +{patientCount - 3} more patients
              </Typography>
            )}
          </Stack>
        ) : (
          <Box
            sx={{
              p: 1.5,
              borderRadius: 3,
              backgroundColor: '#F8FAFC',
              border: '1px dashed rgba(15,23,42,0.16)',
              textAlign: 'center',
            }}
          >
            <Typography variant="body2" color="text.secondary">
              No patient assigned
            </Typography>
          </Box>
        )}
      </Stack>
    </Paper>
  )
}

export default TreatmentZoneCard
